import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  Collapse,
  useTheme,
  Chip,
  Stack,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import UserAccountPopupMenu from './UserAccountPopupMenu';
import { useSubscription } from '../../../features/subscription/hooks/useSubscription';
import { APP_CONFIG } from '../../../config/app.config';
import { Icon, IconSize } from '@shared/icons';

interface MenuDestination {
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  badge?: string;
  action?: (event: React.MouseEvent<HTMLElement>) => void;
  subItems?: MenuDestination[];
}

interface MenuProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

const Menu: React.FC<MenuProps> = ({ open: _drawerOpen, onClose, isMobile }) => {

  const theme = useTheme();

  const navigate = useNavigate();
  const { user, company } = useAuth();
  const location = useLocation();

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const {
    isTrialActive,
    getTrialDaysRemaining,
  } = useSubscription();

  const mainMenuDestinations: MenuDestination[] = [
    {
      label: 'Dashboard',
      icon: <Icon icon="IcDashboard" size={IconSize.LARGE} />,
      active: location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/'),
      action: () => navigate('/'),
    },
    {
      label: 'Sales',
      icon: <Icon icon="Bag01" size={IconSize.LARGE} />,
      active: location.pathname.startsWith('/sales'),
      action: () => navigate('/sales'),
    },
    {
      label: 'Products',
      icon: <Icon icon="Tag" size={IconSize.LARGE} />,
      active: location.pathname.startsWith('/product-catalog'),
      action: () => navigate('/product-catalog/products'),
    },
    {
      label: 'Inventory',
      icon: <Icon icon="Package01" size={IconSize.LARGE} />,
      active: location.pathname.startsWith('/inventory'),
      action: () => navigate('/inventory'),
    },
    {
      label: 'Suppliers',
      icon: <Icon icon="UsersProfiles03" size={IconSize.LARGE} />,
      active: location.pathname.startsWith('/suppliers'),
      action: () => navigate('/suppliers'),
    },
    {
      label: 'Purchasing',
      icon: <Icon icon="Boat" size={IconSize.LARGE} />,
      active: location.pathname.startsWith('/purchasing'),
      action: () => navigate('/purchasing'),
    },
  ];

  const subMenuDestinations: MenuDestination[] = [
    {
      label: user?.name || 'Unnamed user',
      icon: <Icon icon="UserProfile01" size={IconSize.LARGE} />,
      active: userMenuAnchor !== null,
      action: (event: React.MouseEvent<HTMLElement>) => setUserMenuAnchor(event.currentTarget as HTMLElement),
    },
    {
      label: company?.name || 'Unnamed company',
      icon: <Icon icon="Settings" size={IconSize.LARGE} />,
      active: location.pathname.startsWith('/company'),
      action: () => navigate('/company'),
    },
  ];



  if (isMobile) {
    return (
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          height: 64,
        }}
      >
        {mainMenuDestinations.map((destination) => {

          return (
            <Box
              key={destination.label}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
                color: destination.active ? theme.palette.primary.main : theme.palette.text.secondary,
              }}
              onClick={destination.action}
            >
              {destination.icon && (
                destination.icon
              )}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: destination.active ? 700 : 400,
                  fontSize: '0.73rem'
                }}
              >
                {destination.label}
              </Typography>
              {destination.badge && (
                <Box
                  sx={{
                    mt: 0.3,
                    px: 0.8,
                    py: 0.1,
                    fontSize: '0.6rem',
                    background: 'rgba(0,0,0,0.1)',
                    color: 'primary.main',
                    borderRadius: '8px',
                    display: 'inline-block'
                  }}
                >
                  {destination.badge}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  const menuItem = ({ destination }: { destination: MenuDestination }) => (
    <ListItem sx={{ py: 0.5 }}>
      <ListItemButton
        onClick={destination.action}
        selected={destination.active}
        sx={{
          borderRadius: 1,
          px: 1,
          py: 0.75,
          gap: 1.5,
          color: destination.active ? theme.palette.primary.main : theme.palette.text.primary,
          '&:hover': {
            bgcolor: APP_CONFIG.ui.theme.colors.background,
          },
          '&.Mui-selected': {
            bgcolor: APP_CONFIG.ui.theme.colors.primaryTint,
            '& .MuiTypography-root': {
              color: APP_CONFIG.ui.theme.colors.primary,
            },
            '& img': {
              filter: `brightness(0) saturate(100%) invert(48%) sepia(99%) saturate(2122%) hue-rotate(191deg) brightness(102%) contrast(107%)`,
            }
          }
        }}
      >
        {destination.icon && (
          destination.icon
        )}
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                fontWeight={500}
              >
                {destination.label}
              </Typography>

              {destination.badge && (
                <Chip color={destination.active ? 'primary' : 'info'} size="small"
                  label={destination.badge}
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 1,
                    borderColor: APP_CONFIG.ui.theme.colors.primary,
                  }}
                />
              )}
            </Stack>
          }

        />
      </ListItemButton>
    </ListItem>
  )


  const menuList = (items: MenuDestination[]) => (

    <List>
      {items.map((destination, index) => (
        <React.Fragment key={destination.label ?? `${destination.label}-${index}`}>

          {menuItem({ destination })}


          {
            destination.subItems && (
              <Collapse
                in={
                  destination.active || destination.subItems.some((si) => si.active)
                }
                timeout="auto"
              >
                <List component="div" disablePadding sx={{ pl: 2 }}>
                  {destination.subItems.map((subItem) => (
                    menuItem({ destination: subItem })
                  ))}
                </List>
              </Collapse>
            )
          }
        </React.Fragment>
      ))
      }

    </List >

  )

  return (
    <Box
      component="nav"
      sx={{
        width: 220,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        borderRight: '1px solid',
        borderColor: theme.palette.divider,
      }}
    >
      <Box sx={{ px: 2, pt: 4, pb: 2 }}>
        <img
          src="/assets/images/ic_just_stock.png"
          alt="Just Stock"
          style={{
            height: 16,
            objectFit: 'contain',
            alignSelf: 'flex-start',
          }}
        />
      </Box>


      <Stack spacing={2} flexGrow={1}>
        {menuList(mainMenuDestinations)}
      </Stack>

      {isTrialActive() && (

        <Box
          bgcolor={theme.palette.background.default}
          m={2}
          borderRadius={1}
          border={1}
          borderColor={theme.palette.primary.main}
          p={1}
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: theme.palette.background.default,
              borderColor: theme.palette.primary.main,
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }
          }}
          onClick={() => navigate('/company')}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" color={theme.palette.primary.main} sx={{ fontSize: 16, fontWeight: 600 }}>
              {getTrialDaysRemaining()} days left in trial
            </Typography>
          </Stack>
        </Box>

      )}

      <Divider sx={{ mx: 2 }} />

      {menuList(subMenuDestinations)}

      <UserAccountPopupMenu
        anchorEl={userMenuAnchor}
        open={!!userMenuAnchor}
        onClose={() => setUserMenuAnchor(null)}
      />

    </Box>
  );
};

export default Menu; 